<script>
  import UpvoteReactions from './UpvoteReactions.svelte';
  import Header from './Header.svelte';
  import Poll from './Poll.svelte';
  import RowLayout from './Layout/RowLayout.svelte';
  import ColumnLayout from './Layout/ColumnLayout.svelte';
  import { fly } from 'svelte/transition';
  import Footer from './Footer.svelte';
  import WallsWidgetService from './service';
  import CommentFooter from '../Comment/Footer.svelte';
  import CommentAdminService from '../service';
  import { M } from '../../external';
  import { slide } from 'svelte/transition';
  import Comment from '../Comment/Comment.svelte';
  import Input from '../Comment/Input.svelte';

  export let post, onDeletePost, onEditPost;

  const LIMIT_PER_REQUEST = 10;

  const isReactionTypeUpvote = post.settings?.reactionType === 'upvote';
  const layout = post.settings?.layout || 'row';
  const hasPoll = !!post.attachments?.poll;

  let headerComponent;

  let clickedReply = false;

  let hasRepliesFetched = false;
  const childrenPagination = {
    hasNext: null,
    nextTimestamp: null
  };

  let isChildrenCollapsed = false;
  const onCollapseChildren = () => {
    isChildrenCollapsed = true;
  };

  const onExpandChildren = () => {
    isChildrenCollapsed = false;
  };

  const newlyPosted = {
    comments: [],
    checkAndRemoveIfTheyHaveFetched: (comments) => {
      // check if newly posted comments are already fetched and remove them using filter
      newlyPosted.comments = newlyPosted.comments.filter((newlyPostedComment) => {
        return !comments.some((comment) => {
          return comment.id === newlyPostedComment.id;
        });
      });
    }
  };

  const onApprovePost = () => {
    WallsWidgetService.approvePost(post.id);
    post.approved = true;
  };

  const onShowReplies = async () => {
    const response = await CommentAdminService.getAllCommentsByParent(post.id, null, LIMIT_PER_REQUEST, 'asc');
    response.comments = response.comments || [];

    hasRepliesFetched = true;
    childrenPagination.hasNext = response.hasNext;
    childrenPagination.nextTimestamp = response.nextTimestamp;
    post.children = response.comments;
  };

  const onDeleteReply = async (_comment, isNewlyPosted) => {
    const isDeleted = await CommentAdminService.deleteComment(_comment.id);
    // search _comment by `id` in comment.children and remove
    if (isDeleted) {
      if (isNewlyPosted) {
        newlyPosted.comments = newlyPosted.comments.filter((reply) => reply.id !== _comment.id);
      } else {
        post.children = post.children.filter((reply) => reply.id !== _comment.id);
      }

      if (post.children.length === 0 && newlyPosted.comments.length === 0) {
        hasRepliesFetched = false;
      }

      post.replies = post.replies - 1;
    }
  };

  const onShowMore = async () => {
    const response = await CommentAdminService.getAllCommentsByParent(
      post.id,
      childrenPagination.nextTimestamp,
      LIMIT_PER_REQUEST,
      'asc'
    );
    response.comments = response.comments || [];

    childrenPagination.hasNext = response.hasNext;
    childrenPagination.nextTimestamp = response.nextTimestamp;

    // Replies are always sorted in ascending order
    post.children = [...post.children, ...response.comments];
    newlyPosted.checkAndRemoveIfTheyHaveFetched(post.children);
  };

  const onSendReply = async (input, attachments) => {
    const newComment = await CommentAdminService.createCommentByParent(post.id, input, attachments);

    if (!post.children) {
      post.children = [];
    }

    if (post.children.length === 0) {
      hasRepliesFetched = true;
    }

    if (post.children.length < post.replies) {
      // if there are more comments to fetch
      newlyPosted.comments = [...newlyPosted.comments, newComment];
    } else {
      post.children = [...post.children, newComment];
    }
    clickedReply = false;
    post.replies = post.replies + 1;
  };
</script>

<!--class="dark-ui-theme"-->
<div in:fly class="circle-post-wrapper col20 f-left mt-35 mb-15">
  {#if isReactionTypeUpvote}
    <UpvoteReactions {post} />
  {/if}
  <Header
    bind:this={headerComponent}
    {post}
    {onApprovePost}
    {onEditPost}
    {onDeletePost}
    {hasRepliesFetched}
    onCollapse={onCollapseChildren}
    onExpand={onExpandChildren} />

  {#if layout === 'row'}
    <RowLayout {post} />
  {:else}
    <ColumnLayout {post} />
  {/if}

  {#if hasPoll}
    <Poll {post} wrapperClasses="col20 f-left mt-25" />
  {/if}

  {#if isReactionTypeUpvote}
    <Footer
      {post}
      onClickReply={() => (clickedReply = true)}
      {hasRepliesFetched}
      {onShowReplies}
      {isChildrenCollapsed}
      toggleChildren={() => {
        headerComponent.toggle();
      }} />
  {:else}
    <CommentFooter
      comment={post}
      service={WallsWidgetService}
      {hasRepliesFetched}
      {onShowReplies}
      onClickReply={() => (clickedReply = true)}
      allowReplies={true}
      {isChildrenCollapsed}
      toggleChildren={() => {
        headerComponent.toggle();
      }} />
    <!-- Comment Replies-->
  {/if}

  {#if hasRepliesFetched && !isChildrenCollapsed}
    <div transition:slide class="circle-comments-reply-wrapper col20 f-left">
      {#each post.children as reply (reply.id)}
        <Comment
          comment={reply}
          limit={LIMIT_PER_REQUEST}
          onDeleteComment={(_comment) => onDeleteReply(_comment, false)}
          allowReplies={true} />
      {/each}

      {#if newlyPosted.comments.length > 0}
        <div class="col20 f-left mb-15 t-center">
          <a on:click={onShowMore} class="comments-show-all circle-effect">{M.SHOW_MORE_COMMENTS}</a>
        </div>
        {#each newlyPosted.comments as reply (reply.id)}
          <Comment
            comment={reply}
            limit={LIMIT_PER_REQUEST}
            onDeleteComment={(_comment) => onDeleteReply(_comment, true)}
            allowReplies={true} />
        {/each}
      {/if}

      {#if newlyPosted.comments.length === 0 && post.replies > 0 && post.children.length < post.replies}
        <div class="col20 f-left mb-15 t-center">
          <a on:click={onShowMore} class="comments-show-all circle-effect">{M.SHOW_MORE_COMMENTS}</a>
        </div>
      {/if}
    </div>
  {/if}

  <!-- Comment Replies-->
  {#if clickedReply}
    <Input
      service={CommentAdminService}
      author={CommentAdminService.getCurrentAuthor()}
      focused={true}
      onSend={onSendReply}
      allowImages={true} />
  {/if}
</div>
